import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { fetchInternetSpeedLogs, type InternetSpeedLogItem } from '../services/api';

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const formatMetric = (value?: number | null, suffix = '') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const numeric = Number(value);
  return `${numeric.toFixed(numeric >= 100 ? 0 : 1)}${suffix}`;
};

const InternetSpeedLogs: React.FC = () => {
  const [items, setItems] = useState<InternetSpeedLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchInternetSpeedLogs();
      setItems(Array.isArray(data) ? data : []);
    } catch (error: any) {
      Swal.fire('Error', error?.response?.data?.message || 'Failed to load internet speed logs', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;

    return items.filter((item) =>
      [
        item.ipAddress,
        item.connectionType || '',
        formatDateTime(item.createdAt),
        formatMetric(item.downloadMbps, ' Mbps'),
        formatMetric(item.uploadMbps, ' Mbps'),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [items, search]);

  return (
    <Box>
      <Stack
        mb={3}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>Internet Speed</Typography>
          <Typography color="text.secondary">Review internet speed tests with network metrics, IP address, and time.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Search IP, connection, speed..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Button variant="outlined" startIcon={<RefreshRounded />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#F6A938' }}>
              <TableRow>
                <TableCell><strong>Download</strong></TableCell>
                <TableCell><strong>Upload</strong></TableCell>
                <TableCell><strong>Latency</strong></TableCell>
                <TableCell><strong>Connection</strong></TableCell>
                <TableCell><strong>IP Address</strong></TableCell>
                <TableCell><strong>Tested At</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length ? (
                filtered.map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell>{formatMetric(item.downloadMbps, ' Mbps')}</TableCell>
                    <TableCell>{formatMetric(item.uploadMbps, ' Mbps')}</TableCell>
                    <TableCell>{formatMetric(item.unloadedLatencyMs, ' ms')}</TableCell>
                    <TableCell>{item.connectionType || '—'}</TableCell>
                    <TableCell>{item.ipAddress || '—'}</TableCell>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    {loading ? 'Loading internet speed logs…' : 'No internet speed logs found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default InternetSpeedLogs;
